import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import { 
  Users, Building2, Package, Tag, Shield, Settings, Search, 
  ChevronDown, CheckCircle, XCircle, Crown, Zap, ArrowUpDown,
  TrendingUp, ShoppingBag, RefreshCw, AlertCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MerchantManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [activeTab, setActiveTab] = useState('merchants');
  const [merchants, setMerchants] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    checkSuperadminAndLoadData();
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('session_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const checkSuperadminAndLoadData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/superadmin/check`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/merchant/login');
          return;
        }
        throw new Error('Not authorized');
      }

      const data = await response.json();
      if (!data.is_superadmin) {
        toast.error('Superadmin access required');
        navigate('/dashboard');
        return;
      }

      setIsSuperadmin(true);
      await Promise.all([loadMerchants(), loadConsumers(), loadStats()]);
    } catch (error) {
      console.error('Auth check failed:', error);
      toast.error('Access denied');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadMerchants = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/superadmin/merchants`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMerchants(data.merchants || []);
      }
    } catch (error) {
      console.error('Failed to load merchants:', error);
    }
  };

  const loadConsumers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/superadmin/consumers`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConsumers(data.consumers || []);
      }
    } catch (error) {
      console.error('Failed to load consumers:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/superadmin/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleUpdatePlan = async (merchantId, newPlan) => {
    setUpdatingId(merchantId);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/superadmin/merchant/${merchantId}/plan`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ subscription_plan: newPlan })
      });

      if (!response.ok) throw new Error('Failed to update plan');

      toast.success(`Plan updated to ${newPlan}`);
      setShowPlanModal(false);
      setSelectedMerchant(null);
      await loadMerchants();
    } catch (error) {
      toast.error('Failed to update plan');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleToggleStatus = async (type, id, currentStatus) => {
    setUpdatingId(id);
    try {
      const endpoint = type === 'merchant' 
        ? `${API_URL}/api/dashboard/superadmin/merchant/${id}/status`
        : `${API_URL}/api/dashboard/superadmin/consumer/${id}/status`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      toast.success(`${type === 'merchant' ? 'Merchant' : 'Consumer'} ${!currentStatus ? 'activated' : 'deactivated'}`);
      
      if (type === 'merchant') {
        await loadMerchants();
      } else {
        await loadConsumers();
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleToggleSuperadmin = async (merchantId) => {
    setUpdatingId(merchantId);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/superadmin/merchant/${merchantId}/superadmin`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error('Failed to toggle superadmin');

      const data = await response.json();
      toast.success(data.message);
      await loadMerchants();
    } catch (error) {
      toast.error('Failed to toggle superadmin status');
    } finally {
      setUpdatingId(null);
    }
  };

  const getPlanBadge = (plan, isSuperadmin) => {
    if (isSuperadmin) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-400 text-xs font-medium border border-red-500/30">
          <Shield className="w-3 h-3" />
          Superadmin
        </span>
      );
    }
    
    switch (plan) {
      case 'intelligence-suite':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 text-xs font-medium border border-purple-500/30">
            <Crown className="w-3 h-3" />
            Intelligence
          </span>
        );
      case 'pro':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-medium border border-blue-500/30">
            <Zap className="w-3 h-3" />
            Pro
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-500/10 text-gray-400 text-xs font-medium border border-gray-500/30">
            Free
          </span>
        );
    }
  };

  const filteredMerchants = merchants.filter(m =>
    m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredConsumers = consumers.filter(c =>
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1120]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#3EF1C7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#9CB0C7]">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isSuperadmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0B1120]" data-testid="merchant-management-page">
      <MerchantHeader />
      
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-red-400" />
              Merchant Management
            </h1>
            <p className="text-[#9CB0C7] mt-1">Manage all merchants and consumers on the platform</p>
          </div>
          <button
            onClick={() => {
              loadMerchants();
              loadConsumers();
              loadStats();
              toast.success('Data refreshed');
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2A4A6F] transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="rounded-xl border border-[#22304A] bg-[#0E1426]/80 p-6" data-testid="stat-merchants">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-[#9CB0C7]">Total Merchants</p>
                <Building2 className="w-5 h-5 text-[#7A5CFF]" />
              </div>
              <p className="text-3xl font-bold text-white">{stats.merchants?.total || 0}</p>
              <p className="text-xs text-[#8CF78E] mt-1">{stats.merchants?.active || 0} active</p>
            </div>

            <div className="rounded-xl border border-[#22304A] bg-[#0E1426]/80 p-6" data-testid="stat-consumers">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-[#9CB0C7]">Total Consumers</p>
                <Users className="w-5 h-5 text-[#3EF1C7]" />
              </div>
              <p className="text-3xl font-bold text-white">{stats.consumers?.total || 0}</p>
            </div>

            <div className="rounded-xl border border-[#22304A] bg-[#0E1426]/80 p-6" data-testid="stat-products">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-[#9CB0C7]">Total Products</p>
                <Package className="w-5 h-5 text-[#FFC857]" />
              </div>
              <p className="text-3xl font-bold text-white">{stats.products?.total || 0}</p>
            </div>

            <div className="rounded-xl border border-[#22304A] bg-[#0E1426]/80 p-6" data-testid="stat-deals">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-[#9CB0C7]">Active Deals</p>
                <Tag className="w-5 h-5 text-[#8CF78E]" />
              </div>
              <p className="text-3xl font-bold text-white">{stats.deals?.active || 0}</p>
              <p className="text-xs text-[#9CB0C7] mt-1">{stats.deals?.total || 0} total</p>
            </div>
          </div>
        )}

        {/* Plan Distribution */}
        {stats && (
          <div className="rounded-xl border border-[#22304A] bg-[#0E1426]/80 p-6 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Subscription Distribution</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-gray-500/10 border border-gray-500/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#9CB0C7]">Free</span>
                  <span className="text-2xl font-bold text-white">{stats.merchants?.by_plan?.free || 0}</span>
                </div>
                <div className="w-full bg-[#22304A] rounded-full h-2">
                  <div 
                    className="bg-gray-400 h-2 rounded-full" 
                    style={{ width: `${stats.merchants?.total ? (stats.merchants.by_plan.free / stats.merchants.total * 100) : 0}%` }}
                  ></div>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#9CB0C7]">Pro</span>
                  <span className="text-2xl font-bold text-white">{stats.merchants?.by_plan?.pro || 0}</span>
                </div>
                <div className="w-full bg-[#22304A] rounded-full h-2">
                  <div 
                    className="bg-blue-400 h-2 rounded-full" 
                    style={{ width: `${stats.merchants?.total ? (stats.merchants.by_plan.pro / stats.merchants.total * 100) : 0}%` }}
                  ></div>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#9CB0C7]">Intelligence</span>
                  <span className="text-2xl font-bold text-white">{stats.merchants?.by_plan?.intelligence_suite || 0}</span>
                </div>
                <div className="w-full bg-[#22304A] rounded-full h-2">
                  <div 
                    className="bg-purple-400 h-2 rounded-full" 
                    style={{ width: `${stats.merchants?.total ? (stats.merchants.by_plan.intelligence_suite / stats.merchants.total * 100) : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#22304A] pb-4">
          <button
            onClick={() => setActiveTab('merchants')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'merchants'
                ? 'bg-[#7A5CFF] text-white'
                : 'text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]'
            }`}
            data-testid="tab-merchants"
          >
            <Building2 className="w-4 h-4" />
            Merchants ({merchants.length})
          </button>
          <button
            onClick={() => setActiveTab('consumers')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'consumers'
                ? 'bg-[#7A5CFF] text-white'
                : 'text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]'
            }`}
            data-testid="tab-consumers"
          >
            <Users className="w-4 h-4" />
            Consumers ({consumers.length})
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CB0C7]" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white placeholder-[#9CB0C7] focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/50"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Merchants Table */}
        {activeTab === 'merchants' && (
          <div className="rounded-xl border border-[#22304A] bg-[#0E1426]/80 overflow-hidden" data-testid="merchants-table">
            <table className="w-full">
              <thead className="bg-[#111A2E]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Merchant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Products</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Deals</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#22304A]">
                {filteredMerchants.map((m) => (
                  <tr key={m._id} className="hover:bg-[#111A2E]/40" data-testid={`merchant-row-${m._id}`}>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-white">{m.name}</p>
                        <p className="text-sm text-[#9CB0C7]">{m.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getPlanBadge(m.subscription_plan, m.is_superadmin)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white">{m.product_count}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white">{m.deal_count}</span>
                    </td>
                    <td className="px-6 py-4">
                      {m.is_active !== false ? (
                        <span className="flex items-center gap-1 text-[#8CF78E]">
                          <CheckCircle className="w-4 h-4" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[#F55454]">
                          <XCircle className="w-4 h-4" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[#9CB0C7] text-sm">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedMerchant(m);
                            setShowPlanModal(true);
                          }}
                          disabled={updatingId === m._id}
                          className="px-3 py-1 text-xs bg-[#7A5CFF]/20 text-[#7A5CFF] rounded hover:bg-[#7A5CFF]/30 transition-colors disabled:opacity-50"
                          data-testid={`change-plan-${m._id}`}
                        >
                          Change Plan
                        </button>
                        <button
                          onClick={() => handleToggleStatus('merchant', m._id, m.is_active !== false)}
                          disabled={updatingId === m._id}
                          className={`px-3 py-1 text-xs rounded transition-colors disabled:opacity-50 ${
                            m.is_active !== false 
                              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                              : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          }`}
                          data-testid={`toggle-status-${m._id}`}
                        >
                          {m.is_active !== false ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleToggleSuperadmin(m._id)}
                          disabled={updatingId === m._id}
                          className={`px-3 py-1 text-xs rounded transition-colors disabled:opacity-50 ${
                            m.is_superadmin 
                              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                              : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                          }`}
                          title={m.is_superadmin ? 'Revoke superadmin' : 'Grant superadmin'}
                          data-testid={`toggle-superadmin-${m._id}`}
                        >
                          <Shield className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredMerchants.length === 0 && (
              <div className="p-8 text-center text-[#9CB0C7]">
                No merchants found
              </div>
            )}
          </div>
        )}

        {/* Consumers Table */}
        {activeTab === 'consumers' && (
          <div className="rounded-xl border border-[#22304A] bg-[#0E1426]/80 overflow-hidden" data-testid="consumers-table">
            <table className="w-full">
              <thead className="bg-[#111A2E]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Consumer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Auth Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Watchlist</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#22304A]">
                {filteredConsumers.map((c) => (
                  <tr key={c._id} className="hover:bg-[#111A2E]/40" data-testid={`consumer-row-${c._id}`}>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-white">{c.name}</p>
                        <p className="text-sm text-[#9CB0C7]">{c.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-[#22304A] text-[#9CB0C7]">
                        {c.auth_method || 'email'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white">{c.watchlist_count || 0} items</span>
                    </td>
                    <td className="px-6 py-4">
                      {c.is_active !== false ? (
                        <span className="flex items-center gap-1 text-[#8CF78E]">
                          <CheckCircle className="w-4 h-4" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[#F55454]">
                          <XCircle className="w-4 h-4" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[#9CB0C7] text-sm">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-[#9CB0C7] text-sm">
                      {c.last_login ? new Date(c.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus('consumer', c._id, c.is_active !== false)}
                        disabled={updatingId === c._id}
                        className={`px-3 py-1 text-xs rounded transition-colors disabled:opacity-50 ${
                          c.is_active !== false 
                            ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                            : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        }`}
                        data-testid={`toggle-consumer-status-${c._id}`}
                      >
                        {c.is_active !== false ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredConsumers.length === 0 && (
              <div className="p-8 text-center text-[#9CB0C7]">
                No consumers found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Change Plan Modal */}
      {showPlanModal && selectedMerchant && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="plan-modal">
          <div className="bg-[#0E1426] rounded-xl border border-[#22304A] max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-2">Change Subscription Plan</h2>
            <p className="text-[#9CB0C7] text-sm mb-6">
              Update plan for <strong className="text-white">{selectedMerchant.email}</strong>
            </p>
            
            <div className="space-y-3 mb-6">
              <button
                onClick={() => handleUpdatePlan(selectedMerchant._id, 'free')}
                disabled={updatingId === selectedMerchant._id}
                className={`w-full p-4 rounded-lg border transition-colors text-left ${
                  selectedMerchant.subscription_plan === 'free'
                    ? 'border-gray-500 bg-gray-500/10'
                    : 'border-[#22304A] hover:border-gray-500 hover:bg-gray-500/10'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Free</p>
                    <p className="text-xs text-[#9CB0C7]">Basic features, limited usage</p>
                  </div>
                  {selectedMerchant.subscription_plan === 'free' && (
                    <CheckCircle className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>
              
              <button
                onClick={() => handleUpdatePlan(selectedMerchant._id, 'pro')}
                disabled={updatingId === selectedMerchant._id}
                className={`w-full p-4 rounded-lg border transition-colors text-left ${
                  selectedMerchant.subscription_plan === 'pro'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-[#22304A] hover:border-blue-500 hover:bg-blue-500/10'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white flex items-center gap-2">
                      <Zap className="w-4 h-4 text-blue-400" /> Pro
                    </p>
                    <p className="text-xs text-[#9CB0C7]">Advanced analytics, more products</p>
                  </div>
                  {selectedMerchant.subscription_plan === 'pro' && (
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  )}
                </div>
              </button>
              
              <button
                onClick={() => handleUpdatePlan(selectedMerchant._id, 'intelligence-suite')}
                disabled={updatingId === selectedMerchant._id}
                className={`w-full p-4 rounded-lg border transition-colors text-left ${
                  selectedMerchant.subscription_plan === 'intelligence-suite'
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-[#22304A] hover:border-purple-500 hover:bg-purple-500/10'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white flex items-center gap-2">
                      <Crown className="w-4 h-4 text-purple-400" /> Intelligence Suite
                    </p>
                    <p className="text-xs text-[#9CB0C7]">Full access, unlimited everything</p>
                  </div>
                  {selectedMerchant.subscription_plan === 'intelligence-suite' && (
                    <CheckCircle className="w-5 h-5 text-purple-400" />
                  )}
                </div>
              </button>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPlanModal(false);
                  setSelectedMerchant(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg border border-[#22304A] text-white hover:bg-[#1A2437] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
